import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Hourglass } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";

export const ddmm = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", timeZone: "UTC" }) : "");

export const GraceBadge = ({ until, testId }) => (until ? (
  <span className="inline-flex items-center gap-1 rounded-full bg-amber-900/50 px-2 py-0.5 text-[0.7rem] font-semibold text-amber-100" data-testid={testId}>
    <Hourglass className="h-3 w-3 shrink-0" /> En période de grâce jusqu'au {ddmm(until)}</span>
) : null);

// Renewal grace (all store subscriptions): access kept N days after an unpaid / late renewal.
export const GraceCard = () => {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  const [days, setDays] = useState("");
  const [sent, setSent] = useState([]);
  useEffect(() => {
    api.get("/admin/billing/grace").then(({ data }) => { setS(data); setDays(String(data.days)); }).catch(() => {});
    api.get("/admin/billing/grace/reminders", { params: { limit: 30 } }).then(({ data }) => setSent(data)).catch(() => {});
  }, []);
  const ok = days !== "" && Number(days) >= 0 && Number(days) <= 60;
  const save = () => api.put("/admin/billing/grace", { days: Number(days) })
    .then(({ data }) => { setS(data); toast.success(t("grace.saved")); }).catch((err) => toast.error(translateError(err, t)));
  if (!s) return null;
  return (
    <section className="card-carbon min-w-0 space-y-3 rounded-2xl p-5" data-testid="grace-card">
      <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-[var(--gold-bright)]"><Hourglass className="h-4 w-4 shrink-0" /> {t("grace.title")}</h2>
      <p className="break-words text-xs text-neutral-400">{t("grace.hint")}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Input type="number" min={0} max={60} value={days} onChange={(e) => setDays(e.target.value)} className="h-9 w-24 bg-black/50" data-testid="grace-days-input" />
        <span className="text-sm text-neutral-300">{t("grace.days")}</span>
        <button type="button" className="btn-gold btn-sm min-h-[40px]" disabled={!ok} onClick={save} data-testid="grace-days-save">{t("novelty.save")}</button>
      </div>
      {s.items.length ? (
        <ul className="min-w-0 space-y-2 text-sm" data-testid="grace-list">
          {s.items.map((x) => (
            <li key={`${x.kind}:${x.id}`} className="min-w-0 break-words rounded-xl border border-amber-400/30 bg-amber-950/20 p-2 text-neutral-200" data-testid={`grace-item-${x.id}`}>
              <span className="[overflow-wrap:anywhere]">{x.email}</span>{x.refs?.filter(Boolean).length ? ` · ${x.refs.filter(Boolean).join(", ")}` : ""} <GraceBadge until={x.grace_until} testId={`grace-item-until-${x.id}`} />
              <span className="ml-1 text-xs text-neutral-400" data-testid={`grace-item-reminders-${x.id}`}>· Rappels envoyés : {x.reminders || 0}</span>
            </li>
          ))}
        </ul>
      ) : <p className="text-xs text-neutral-500" data-testid="grace-empty">{t("grace.none")}</p>}
      {sent.length > 0 && (
        <details className="min-w-0 text-xs text-neutral-300" data-testid="grace-reminders">
          <summary className="cursor-pointer font-semibold text-neutral-200">Rappels de paiement envoyés ({sent.length})</summary>
          <ul className="mt-2 space-y-1">
            {sent.map((r) => (
              <li key={r.id} className="break-words [overflow-wrap:anywhere]" data-testid={`grace-reminder-${r.id}`}>
                {ddmm(r.at)} · {r.email}{r.refs?.filter(Boolean).length ? ` · ${r.refs.filter(Boolean).join(", ")}` : ""} · n°{r.n}{r.last ? " (dernier jour)" : ""} · {(r.channels || []).join(" + ")}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
};
